// CASLContext.jsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { createMongoAbility } from "@casl/ability";
import { useSelector } from "react-redux";
import { selectUser } from "state/reducer/user";

const CASLContext = createContext(null);

export const useCASL = () => {
  const context = useContext(CASLContext);
  if (!context) {
    throw new Error("useCASL must be used within CASLProvider");
  }
  return context;
};

export function CASLProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [ability, setAbility] = useState(createMongoAbility([]));
  const [loading, setLoading] = useState(true);

  const profile = useSelector(selectUser).getProfile;

  /**
   * Logout
   */
  const logout = useCallback(() => {
    // apiClient.logout();
    setUser(null);
    setCompany(null);
    setAbility(createMongoAbility([]));
  }, []);

  /**
   * Fetch user and abilities from backend
   * Backend sends pre-computed CASL rules
   */
  const fetchUserAndAbilities = useCallback(async () => {
    try {
      setLoading(true);

      // Get user data with abilities from backend
      // const data = await apiClient.getCurrentUser();

      setUser(profile?.data);
      setCompany(profile?.data?.company);

      // Use backend-computed abilities (matches defineAbilitiesFor)
      if (profile?.data?.abilities) {
        const userAbility = createMongoAbility(profile.data.abilities);
        setAbility(userAbility);
      } else {
        // Fallback: empty abilities
        setAbility(createMongoAbility([]));
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      if (
        error.message.includes("401") ||
        error.message.includes("Authentication")
      ) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [profile?.data, logout]);

  useEffect(() => {
    if (profile?.data) {
      fetchUserAndAbilities();
    } else {
      setLoading(false);
    }
  }, [fetchUserAndAbilities]);

  /**
   * Login (matches backend authenticate middleware)
   */
  const login = async (email, password, companySlug) => {
    try {
      // const data = await apiClient.login(email, password, companySlug);

      // setUser(data.user);
      // setCompany(data.company);

      // // Set abilities from login response
      // if (data.abilities && data.abilities.rules) {
      //   const userAbility = createMongoAbility(data.abilities.rules);
      //   setAbility(userAbility);
      // }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Refresh abilities (call after role changes)
   */
  const refresh = async () => {
    await fetchUserAndAbilities();
  };

  // CASL ability checks
  const can = (action, subject, field = undefined) => {
    if (!ability) return false;

    return ability.can(action, subject, field);
  };

  const cannot = (action, subject, field = undefined) => {
    if (!ability) return true;
    return ability.cannot(action, subject, field);
  };

  const canAny = (actions, subject, field = undefined) => {
    return actions.some((action) => can(action, subject, field));
  };

  const canAll = (actions, subject, field = undefined) => {
    return actions.every((action) => can(action, subject, field));
  };

  // Admin checks
  const isCompanyAdmin = () =>
    user?.isCompanyAdmin || user?.isSuperAdmin || false;
  const isSuperAdmin = () => user?.isSuperAdmin || false;

  const value = {
    user,
    company,
    loading,
    ability,
    can,
    cannot,
    canAny,
    canAll,
    isCompanyAdmin,
    isSuperAdmin,
    login,
    logout,
    refresh,
    fetchUserAndAbilities,
    // apiClient, // Expose API client
  };

  return <CASLContext.Provider value={value}>{children}</CASLContext.Provider>;
}

export const ACTIONS = {
  MANAGE: "manage",
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
};

export const SUBJECTS = {
  ALL: "all",
  USER: "User",
  ROLE: "Role",
  PERMISSION: "Permission",
  WORK_UNIT: "WorkUnit",
  MITRA: "Mitra",
  PROPERTY: "Property",
  BUILDING: "Building",
  FLOOR: "Floor",
  UNIT: "Unit",
};
